(function () {
  const selector = 'video[data-wvr-lazy-video]';
  const observed = new WeakSet();
  const visibility = new WeakMap();

  const reducedMotionQuery =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

  function prefersReducedMotion() {
    return !!(reducedMotionQuery && reducedMotionQuery.matches);
  }

  function loadSources(video) {
    if (video.dataset.wvrLoaded === 'true') return false;
    let changed = false;
    video.querySelectorAll('source[data-src]').forEach(function (source) {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
      changed = true;
    });
    if (video.dataset.src && !video.getAttribute('src')) {
      video.src = video.dataset.src;
      video.removeAttribute('data-src');
      changed = true;
    }
    video.dataset.wvrLoaded = 'true';
    // load() runs the media load algorithm, which aborts any play already in
    // flight. The theme renders concrete <source src>, so nothing is deferred
    // and reloading here would swallow the user's first click on the control.
    if (changed) video.load();
    return changed;
  }

  // A pause or a play this script triggers itself must not be read as a user
  // action. The events fire asynchronously, so a marker has to survive until the
  // handler consumes it; it is only set when the event will actually follow, so
  // it can never leak onto a later user-initiated one.
  const programmaticPauses = new WeakMap();
  const programmaticPlays = new WeakMap();

  function mark(marker, video) {
    marker.set(video, (marker.get(video) || 0) + 1);
  }

  function unmark(marker, video) {
    const pending = marker.get(video) || 0;
    if (pending <= 1) marker.delete(video);
    else marker.set(video, pending - 1);
  }

  function consume(marker, video) {
    if ((marker.get(video) || 0) <= 0) return false;
    unmark(marker, video);
    return true;
  }

  function pauseProgrammatically(video) {
    if (video.paused) return;
    mark(programmaticPauses, video);
    video.pause();
  }

  // User ownership is proven, never inferred from absence. These same nodes are
  // also driven by assets/video-muted.js, which is loaded globally and calls
  // play() straight out of its own IntersectionObserver; reading every unmarked
  // play as the user's therefore handed that script permanent user priority and
  // a standing exemption from every reduced-motion pause, so autoplay survived
  // the preference. A play only counts as the user's when a trusted interaction
  // on this very video immediately preceded it. Everything else — foreign
  // script, unknown origin, engine we cannot read — is handled as autoplay.
  //
  // Support audit for the signals this relies on:
  //  - event.isTrusted: Chrome 46+, Firefox 35+, Safari 10+. Where it is missing
  //    or false the interaction is not counted, so attribution fails closed.
  //  - Native control clicks: the controls live in a shadow/UA-widget tree in
  //    Chromium, Firefox and WebKit alike, and the event is retargeted to the
  //    host <video>, so a listener on the element does see them. Registering in
  //    the capture phase means it is seen before anything inside can stop
  //    propagation. Where an engine hides the interaction anyway, the play is
  //    treated as autoplay rather than credited to the user.
  //  - Pointer Events are unavailable in older WebKit, so mousedown/touchstart
  //    are listened for beside pointerdown.
  //  - navigator.userActivation is deliberately NOT used: it is page-wide and
  //    stays active for seconds after a click anywhere, so it cannot tell this
  //    video's play apart from a foreign script's, and consuming activation
  //    elsewhere would falsely deny a real one.
  const USER_INTENT_WINDOW_MS = 1000;
  const userIntent = new WeakMap();

  function now() {
    return typeof performance === 'object' && performance && typeof performance.now === 'function'
      ? performance.now()
      : Date.now();
  }

  function noteUserIntent(video) {
    userIntent.set(video, now());
  }

  // The intent is consumed, so one interaction can vouch for one play and never
  // for a script's play that happens to follow it later.
  function consumeUserIntent(video) {
    const at = userIntent.get(video);
    userIntent.delete(video);
    return typeof at === 'number' && now() - at <= USER_INTENT_WINDOW_MS;
  }

  // Space and Enter are what the native controls act on when the video or a
  // control inside it holds focus; the media keys are the other explicit ask.
  function isPlaybackKey(event) {
    if (typeof event.key === 'string') {
      return (
        event.key === ' ' ||
        event.key === 'Spacebar' ||
        event.key === 'Enter' ||
        event.key === 'MediaPlayPause' ||
        event.key === 'MediaPlay'
      );
    }
    return event.keyCode === 32 || event.keyCode === 13;
  }

  function allVideos() {
    return Array.prototype.slice.call(document.querySelectorAll(selector));
  }

  // Playback is elected per surface, not per page. A surface is the nearest
  // ancestor that holds more than one lazy video — the row or grid the videos
  // were authored into — and a video with no such ancestor is its own surface.
  // A page-wide election was the regression: one row's winner silenced every
  // other row on the same page, and the losers ship preload="none", so they were
  // left with no frame, no controls and nothing reachable at all.
  function surfaceOf(video, videos) {
    let node = video.parentElement;
    while (node) {
      let count = 0;
      for (let i = 0; i < videos.length; i++) {
        if (node.contains(videos[i])) {
          count += 1;
          if (count > 1) return node;
        }
      }
      node = node.parentElement;
    }
    return video;
  }

  function surfaces(videos) {
    const groups = new Map();
    const grouped = [];
    videos.forEach(function (video) {
      const key = surfaceOf(video, videos);
      const members = groups.get(key);
      if (members) {
        members.push(video);
      } else {
        const created = [video];
        groups.set(key, created);
        grouped.push(created);
      }
    });
    return grouped;
  }

  function surfaceMembers(video) {
    const videos = allVideos();
    const key = surfaceOf(video, videos);
    if (key === video) return [video];
    return videos.filter(function (other) {
      return surfaceOf(other, videos) === key;
    });
  }

  function pauseOthers(active, members) {
    (members || surfaceMembers(active)).forEach(function (video) {
      if (video === active) return;
      pauseProgrammatically(video);
    });
  }

  function surfaceUserPlaying(exclude) {
    return surfaceMembers(exclude).some(function (video) {
      return video !== exclude && !video.paused && video.dataset.wvrUserPlaying === 'true';
    });
  }

  function startVideo(video, members) {
    loadSources(video);
    pauseOthers(video, members);

    mark(programmaticPlays, video);
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {
        // A rejected play() fires no play event, so the marker has to be
        // released here or it would misread the user's next play as ours.
        unmark(programmaticPlays, video);
      });
    }
  }

  // Only one video per surface autoplays, and reduced motion autoplays none, so
  // every other lazy video needs its own still. These videos ship
  // preload="none" and the UGC bubbles carry neither poster nor controls, so
  // without this they stay blank boxes with no reachable representation of their
  // content. The still is the video's own first frame — no poster image, media
  // or rights claim is introduced here. An authored poster is left alone, which
  // also leaves that surface's byte budget exactly as it was.
  function ensureStillFrame(video) {
    if (video.dataset.wvrStill === 'true' || !video.paused) return;
    video.dataset.wvrStill = 'true';
    if (video.getAttribute('poster')) return;

    const reloaded = loadSources(video);
    if (video.getAttribute('preload') === 'none') video.preload = 'metadata';
    // readyState 0 means nothing has been fetched yet, so neither the load() nor
    // the start position below can interrupt playback or discard a position the
    // user scrubbed to.
    if (video.readyState !== 0) return;
    if (!reloaded) video.load();
    try {
      // Safari decodes no frame for a video that is never played unless a start
      // position is set. Below HAVE_METADATA this only records the default
      // playback start position; it queues no seek.
      video.currentTime = 0.001;
    } catch (e) {
      // Engines that reject a pre-metadata seek still paint the frame that
      // preload="metadata" fetches.
    }
  }

  function mostVisibleVideo(members) {
    let best = null;
    let bestRatio = 0;
    members.forEach(function (video) {
      const ratio = visibility.get(video) || 0;
      if (ratio > bestRatio) {
        bestRatio = ratio;
        best = video;
      }
    });
    return bestRatio > 0 ? best : null;
  }

  function electTarget(members, reduced) {
    // Reduced motion starts nothing, and an explicit user play owns its surface
    // until it ends — the visibility heuristic must not take the surface back.
    if (reduced) return null;
    let userPlaying = false;
    members.forEach(function (video) {
      if (!video.paused && video.dataset.wvrUserPlaying === 'true') userPlaying = true;
    });
    if (userPlaying) return null;

    const best = mostVisibleVideo(members);
    if (best && best.dataset.wvrUserPaused === 'true') return null;
    return best;
  }

  let syncFrame;
  function syncPlayback() {
    cancelAnimationFrame(syncFrame);
    syncFrame = requestAnimationFrame(function () {
      const videos = allVideos();
      const reduced = prefersReducedMotion();

      videos.forEach(function (video) {
        // A video that has scrolled out of view is paused regardless of how it started.
        if ((visibility.get(video) || 0) <= 0) pauseProgrammatically(video);
      });

      surfaces(videos).forEach(function (members) {
        const target = electTarget(members, reduced);

        members.forEach(function (video) {
          if ((visibility.get(video) || 0) <= 0) return;

          if (video === target) {
            if (video.paused) startVideo(video, members);
            else pauseOthers(video, members);
            return;
          }

          // Reduced motion stops anything this script started; a play the user
          // asked for is left running.
          if (!video.paused && reduced && video.dataset.wvrUserPlaying !== 'true') {
            pauseProgrammatically(video);
          }

          if (video.paused) ensureStillFrame(video);
        });
      });
    });
  }

  const observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        visibility.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
      });
      syncPlayback();
    },
    {
      rootMargin: '0px 0px',
      threshold: [0, 0.25, 0.5, 0.75, 1]
    }
  );

  // Capture, so a control that stops propagation inside the video's own shadow
  // tree cannot hide the interaction; passive, so recording it can never delay a
  // scroll. Engines that predate the options object read it as a truthy
  // useCapture, which is the half that matters here.
  const intentOptions = { capture: true, passive: true };

  function trackUserIntent(video) {
    ['pointerdown', 'mousedown', 'touchstart', 'click'].forEach(function (type) {
      video.addEventListener(
        type,
        function (event) {
          if (event.isTrusted === true) noteUserIntent(video);
        },
        intentOptions
      );
    });
    video.addEventListener(
      'keydown',
      function (event) {
        if (event.isTrusted === true && isPlaybackKey(event)) noteUserIntent(video);
      },
      intentOptions
    );
  }

  function trackVideo(video) {
    if (observed.has(video)) return;
    observed.add(video);
    trackUserIntent(video);
    // A user-initiated play always wins over the visibility heuristic, and it
    // still pauses the other videos on its own surface so only one plays there.
    // A play that is neither ours nor attributable to the user gets none of
    // that: it is autoplay, whichever script asked for it.
    video.addEventListener('play', function () {
      const ours = consume(programmaticPlays, video);
      const userRequested = !ours && consumeUserIntent(video);

      if (userRequested) {
        delete video.dataset.wvrUserPaused;
        video.dataset.wvrUserPlaying = 'true';
      } else {
        delete video.dataset.wvrUserPlaying;
      }

      loadSources(video);

      if (!userRequested && prefersReducedMotion()) {
        // The election starts nothing under the preference, so this play came
        // from elsewhere. Stopping it here rather than waiting for the next
        // intersection is what keeps autoplay suppressed on load.
        pauseProgrammatically(video);
        syncPlayback();
        return;
      }

      if (!ours && !userRequested && surfaceUserPlaying(video)) {
        // An unattributable play must not take a surface away from the video the
        // user actually started, and letting both run would break one-per-surface.
        pauseProgrammatically(video);
        return;
      }

      pauseOthers(video);
      // A foreign play leaves the surface in a state the election never chose;
      // reconcile it instead of trusting it.
      if (!ours) syncPlayback();
    });
    video.addEventListener('pause', function () {
      delete video.dataset.wvrUserPlaying;
      if (consume(programmaticPauses, video)) return;
      if ((visibility.get(video) || 0) > 0) video.dataset.wvrUserPaused = 'true';
    });
    observer.observe(video);
  }

  function observeVideos(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(selector).forEach(trackVideo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      observeVideos(document);
    });
  } else {
    observeVideos(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    observeVideos(event.target);
  });

  if (reducedMotionQuery) {
    const onPreferenceChange = function () {
      if (prefersReducedMotion()) {
        allVideos().forEach(function (video) {
          if (video.dataset.wvrUserPlaying !== 'true') pauseProgrammatically(video);
        });
      }
      syncPlayback();
    };
    if (typeof reducedMotionQuery.addEventListener === 'function') {
      reducedMotionQuery.addEventListener('change', onPreferenceChange);
    } else if (typeof reducedMotionQuery.addListener === 'function') {
      reducedMotionQuery.addListener(onPreferenceChange);
    }
  }

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches(selector)) {
          trackVideo(node);
        }
        observeVideos(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
